import { useEffect, useState } from 'react';
import { apiRequest } from '../lib/api';
import type { Attachment } from '../lib/types';
import { browserWorkspace } from '../lib/workspace';

type Status = NonNullable<Attachment['scanStatus']>;

export function useAttachmentScanStatuses(attachments: Attachment[]) {
  const [updates, setUpdates] = useState<Record<string, { source: Attachment['scanStatus']; status: Status }>>({});
  const [refreshError, setRefreshError] = useState(false);
  const statusOf = (attachment: Attachment): Status => {
    const update = updates[attachment.id];
    return (update && update.source === attachment.scanStatus ? update.status : attachment.scanStatus)
      || (attachment.downloadUrl ? 'clean' : 'pending');
  };
  const pendingIds = attachments.filter((attachment) => !attachment.scanBypassed && statusOf(attachment) === 'pending').map((a) => a.id).sort().join(',');
  const sources = JSON.stringify(attachments.map((a) => [a.id, a.scanStatus]));

  useEffect(() => {
    if (!pendingIds) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const controller = new AbortController();
    const workspace = browserWorkspace();
    const sourceById = new Map<string, Attachment['scanStatus']>(JSON.parse(sources));
    const poll = async () => {
      let delay = 5_000;
      try {
        if (document.visibilityState !== 'hidden') {
          const ids = pendingIds.split(',');
          for (let offset = 0; offset < ids.length; offset += 50) {
            const result = await apiRequest<{ statuses: Array<{ id: string; scanStatus: Status }> }>(
              `/attachments/scan-status?ids=${encodeURIComponent(ids.slice(offset, offset + 50).join(','))}`,
              { signal: controller.signal, headers: { "X-Support-Workspace": workspace } },
            );
            if (stopped) return;
            setUpdates((current) => {
              const next = { ...current };
              for (const row of result.statuses) next[row.id] = { source: sourceById.get(row.id) ?? undefined, status: row.scanStatus };
              return next;
            });
          }
          setRefreshError(false);
        }
      } catch {
        if (!stopped) setRefreshError(true);
        delay = 15_000;
      }
      if (!stopped) timer = setTimeout(() => void poll(), delay);
    };
    timer = setTimeout(() => void poll(), 1_000);
    return () => { stopped = true; clearTimeout(timer); controller.abort(); };
  }, [pendingIds, sources]);

  function updateStatus(attachment: Attachment, status: Status) {
    setRefreshError(false);
    setUpdates((current) => ({ ...current, [attachment.id]: { source: attachment.scanStatus, status } }));
  }
  return { statusOf, updateStatus, refreshError };
}
